import { PossibleAnswer, Question, QuestionTemplate } from '@amzn/awscat-portfolio-assessment-types';
import { FormField, Input, NonCancelableCustomEvent, RadioGroup, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';

import { constants } from '../../../common/Localization';
import { deepCopy } from '../../../common/util';
import { capitalizeWord } from '../../portfolio-data-asset/utils';
import { questionTypes } from './constants';
import DataCollectionEditPossibleAnswers from './DataCollectionEditPossibleAnswers';

type Q = Question | QuestionTemplate;
interface Props<T> {
    question: T;
    updateQuestion: (currentQuestion: T, updatedQuestion: T) => void;
    activeTab: string;
    propertyOptions: OptionDefinition[];
}

function DataCollectionEditQuestion<T extends Q>({ activeTab, question, propertyOptions, updateQuestion }: Props<T>): JSX.Element {

    const updatePossibleAnswers = (possibleAnswers: PossibleAnswer[]) => {
        updateQuestion(question, {...question, possibleAnswers: possibleAnswers});
    };

    const addPossibleAnswer = () => {
        const updatedQuestion: T = deepCopy(question);
        if (!updatedQuestion.possibleAnswers) {
            updatedQuestion.possibleAnswers = [];
        }

        updatedQuestion.possibleAnswers.push({
            label: '', value: ''
        });
        updateQuestion(question, updatedQuestion);
    };

    const deletePossibleAnswer = (possibleAnswer: PossibleAnswer) => {
        const index = question.possibleAnswers?.findIndex(answers => answers === possibleAnswer);
        if (index !== undefined && index > -1) {
            const updatedQuestion: T = deepCopy(question);
            updatedQuestion.possibleAnswers?.splice(index, 1);
            updateQuestion(question, updatedQuestion);
        }   
    };

    const getSelectedProperty = () => {
        if (question.hasOwnProperty('propertyReference')) {
            return propertyOptions.find(option => option.value === (question as Question).propertyReference) ?? null;
        } else {
            return propertyOptions.find(option => option.value === question.resourceReference) ?? null;
        }
    };

    const updateSelectedProperty = ({ detail }: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        if (question.hasOwnProperty('propertyReference')) {
            updateQuestion(question, {...question, propertyReference: detail.selectedOption.value ?? ''});
        } else {
            updateQuestion(question, {...question, resourceReference: detail.selectedOption.value ?? ''});
        }
    };

    return (
        <SpaceBetween size='s'>
            <FormField
                label={constants.QUESTION_LABEL}
            >
                <Input
                    value={question.label}
                    onChange={({ detail }) => updateQuestion(question, {...question, label: detail.value})}
                />
            </FormField>
            <FormField
                label={`${capitalizeWord(activeTab)} ${constants.PROPERTY}`}
            >
                <Select
                    options={propertyOptions}
                    selectedOption={getSelectedProperty()}
                    onChange={updateSelectedProperty}
                ></Select>
            </FormField>
            <FormField
                label={constants.QUESTION_TEXT}
            >
                <Input
                    value={question.questionText}
                    onChange={({ detail }) => updateQuestion(question, {...question, questionText: detail.value})}
                />
            </FormField>
            <FormField
                label={constants.QUESTION_TYPE}
            >
                <RadioGroup
                    value={question.answerType}
                    items={questionTypes}
                    onChange={({ detail }) => updateQuestion(question, {...question, answerType: detail.value})}
                />
            </FormField>
            { question.answerType === 'select' ? (
                <DataCollectionEditPossibleAnswers 
                    possibleAnswers={question.possibleAnswers ?? []}
                    addPossibleAnswer={addPossibleAnswer}
                    deletePossibleAnswer={deletePossibleAnswer}
                    updatePossibleAnswers={updatePossibleAnswers}
                />
            ) : (
                <Input disabled={true} value={ question.hasOwnProperty('hint') ? (question as Question).hint : ''} />
            )}
        </SpaceBetween>
    );
}

export default DataCollectionEditQuestion;
